import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect, Loader } from '@mantine/core';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import SettingsCard from '@/components/settings-card';
import { localTenant, updateMe } from '@/services/session-service';
import { Tenant } from '@turizi/shared';
import { setWebTitle } from '@/lib/format-utils';
import { useUpdateTenant, useUsers } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';


export default function SettingsNotificationsPage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    const updateTenantMutation = useUpdateTenant();
    const { data: usersResponse, isLoading: usersLoading } = useUsers();

    const [draft, setDraft] = useState<Tenant | null>(tenant);

    setWebTitle(t('notifications_settings'));

    const users = usersResponse?.data || [];
    const selectData = users.map(u => ({
        value: String(u.id),
        label: u.name ? `${u.name} (${u.email})` : u.email
    }));

    const receiversValue = (draft?.settings?.notifications?.payment_request_receivers || []).map(String);

    const handleSave = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                notifications: {
                    payment_request_receivers: receiversValue.map(Number)
                }
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success'), t('success'));
            },
            onError: (err: any) => {
                notify.error(err?.message || t('error_saving_settings'), t('error'));
            }
        });
    };

    const handleReceiversChange = (value: string[]) => {
        setDraft(prev => {
            if (!prev) return null;
            const currentNotifications = prev.settings.notifications || {};
            return {
                ...prev,
                settings: {
                    ...prev.settings,
                    notifications: {
                        ...currentNotifications,
                        payment_request_receivers: value.map(Number)
                    }
                }
            };
        });
    };

    return (
        <div className="panel-content max-w-5xl">
            <PageHeader title={t('settings')} />

            <SettingsLayout>
                <div className='flex flex-col gap-6 w-full'>
                    <SettingsCard
                        title={t('notifications_settings')}
                        description={t('notifications_settings_desc')}
                        onSave={handleSave}
                        saving={updateTenantMutation.isPending}
                    >
                        <div className="space-y-4">
                            {usersLoading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                                    <Loader size="xs" />
                                    <span>{t('loading_users')}</span>
                                </div>
                            ) : (
                                <MultiSelect
                                    label={t('payment_request_receivers_label')}
                                    description={t('payment_request_receivers_desc')}
                                    placeholder={t('select_users')}
                                    data={selectData}
                                    value={receiversValue}
                                    onChange={handleReceiversChange}
                                    searchable
                                    clearable
                                    nothingFoundMessage={t('no_users_found')}
                                />
                            )}
                        </div>
                    </SettingsCard>
                </div>
            </SettingsLayout>
        </div>
    );
}
